import React, { useEffect, useRef } from "react";
import useInfiniteSearch from "hooks/useInfiniteSearch";
import { reorderListingsBasedOnStatus } from "features/Listings/utils/reorderListingsBasedOnStatus";
import { OFFERING_STATUSES } from "constants/schedule";
import { useOfferings } from "hooks/useOfferings";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from "../Listings.constants";

const default_offering_status_to_show = [OFFERING_STATUSES.live.value];
const defaultFormatOptions = (options) =>
  options?.map((listing) => ({
    ...listing,
    value: listing.uuid, // we always need this prop to define the value that will be passed to onChange function
  }));

// TODO: add functionality to fetch details of the selected offering if its not in the 1st page itself
// reference
//    - src/schedule-v2/modals/recorded-content/AddLiveSession/useLiveSessionOfferingOptions.js
//    - author @harshit
/**
 * hook uses InfiniteSearch to call all pages of the offering list
 * to be used in combination with ExlyOfferingSelect component
 */
/**
 * @typedef {Object} InfiniteListingOptionsProps
 * @property {(number | undefined)[]=} offeringStatusesToShow
 * @property {(options: any) => any=} formatOptions
 * @property {Object=} listingApiPayload
 * @property {boolean=} isOfferingVariantsEnabled
 */

/**
 * @param {InfiniteListingOptionsProps} props
 */
export const useInfiniteListingOptions = ({
  offeringStatusesToShow = default_offering_status_to_show,
  formatOptions = defaultFormatOptions,
  isOfferingVariantsEnabled,
  listingApiPayload = {},
  hideMerchVariantsEnabledListing = false,
} = {}) => {
  const {
    data: options,
    loading,
    setLoading,
    setError,
    setHasMore,
    pageNumber,
    setData,
    lastElementRef,
    setPageNumber,
  } = useInfiniteSearch();

  const payloadString = JSON.stringify(listingApiPayload);
  const prevPayloadStringRef = useRef(payloadString);

  useEffect(() => {
    if (prevPayloadStringRef.current !== payloadString) {
      prevPayloadStringRef.current = payloadString;
      setPageNumber(DEFAULT_PAGE_NUMBER);
      setData([]);
    }
  }, [payloadString, setPageNumber, setData]);

  const { fetchOfferingsList } = useOfferings();

  React.useEffect(() => {
    setLoading(true);
    setError(false);

    const statusString = offeringStatusesToShow.join(",");
    const payload = {
      page: pageNumber,
      page_size: DEFAULT_PAGE_SIZE,
      status: statusString,
      hide_merch_variants_enabled_listings: hideMerchVariantsEnabledListing,
      ...listingApiPayload,
    };
    if (isOfferingVariantsEnabled !== undefined) {
      payload.is_offering_variants_enabled = isOfferingVariantsEnabled;
    }
    fetchOfferingsList(payload)
      .then((response) => {
        const { listings, total_pages } = response?.data || {};
        setHasMore(total_pages > pageNumber);
        setData((prevData) => {
          const formatted = formatOptions(listings);
          const merged =
            pageNumber === DEFAULT_PAGE_NUMBER
              ? formatted
              : [...prevData, ...formatted];
          return reorderListingsBasedOnStatus(merged, offeringStatusesToShow);
        });
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [pageNumber, payloadString]);

  return {
    loading,
    options,
    lastElementRef,
  };
};
